import { type ReactNode, useState } from 'react';
import { ArrowsMergeIcon, InfoIcon, LinkIcon } from '@phosphor-icons/react';
import { Modal, ModalActionType, ModalVariant } from '@/components/Modal/Modal';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';

interface MergeIdentityModalProps {
    isOpen: boolean;
    onClose: () => void;
    titleText: string;
    infoText: string;
    submitText: string;
    isLink: boolean;
    renderSource: () => ReactNode;
    renderTargetPicker: (args: {
        selectedId: string | null;
        onSelect: (id: string) => void;
        onClear: () => void;
    }) => ReactNode;
    onConfirm: (selectedId: string) => Promise<void>;
}

export function MergeIdentityModal({
    isOpen,
    onClose,
    titleText,
    infoText,
    submitText,
    isLink,
    renderSource,
    renderTargetPicker,
    onConfirm,
}: MergeIdentityModalProps) {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClear = () => setSelectedId(null);

    const handleClose = () => {
        handleClear();
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedId) return;
        setIsSubmitting(true);
        try {
            await onConfirm(selectedId);
            handleClose();
        } catch {
            // Keep the modal open so the user can retry or adjust the selection.
        } finally {
            setIsSubmitting(false);
        }
    };

    const title = (
        <div className="flex items-center gap-1.5">
            {titleText}
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        tabIndex={-1}
                        className="text-grey-300 outline-none hover:text-grey-200"
                    >
                        <InfoIcon size={15} />
                    </button>
                </TooltipTrigger>
                <TooltipContent>{infoText}</TooltipContent>
            </Tooltip>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            onCancel={handleClose}
            onSubmit={() => void handleSubmit()}
            title={title}
            submitText={submitText}
            submitDisabled={!selectedId}
            submitInProgress={isSubmitting}
            actionType={ModalActionType.SECONDARY}
            variant={ModalVariant.PLAIN}
            size="small"
        >
            <div className="flex flex-col gap-4 px-6 py-4">
                {renderSource()}

                <div className="flex items-center justify-center">
                    {isLink ? (
                        <LinkIcon className="text-grey-300" size={20} />
                    ) : (
                        <ArrowsMergeIcon className="text-grey-300" size={20} />
                    )}
                </div>

                {renderTargetPicker({
                    selectedId,
                    onSelect: setSelectedId,
                    onClear: handleClear,
                })}
            </div>
        </Modal>
    );
}
